import { Controller } from '@hotwired/stimulus'
import {
  shouldSkipCheckForChanges,
  type CustomChangeBroadcastPayload,
  fieldIsMultiple,
  listenToChangesOnForm
} from '../_broadcast_change_utils'

type EmptyObject = Record<PropertyKey, never>
type Conditions = Record<string, string | string[]>

const isMatched = (value: string, reference: string | Array<string | File>): boolean => {
  if (Array.isArray(reference)) {
    return reference.includes(value)
  }
  if (reference === 'non_blank') {
    return value !== ''
  }
  if (reference === 'blank') {
    return value === ''
  }
  return value === reference
}

const conditionChecker =
  (isMultiple: boolean, formData: FormData) =>
  (condition: [string, string | string[]]): boolean => {
    const [conditionFieldName, conditionValue] = condition
    // Multiple values on the form for this field
    if (isMultiple) {
      const formValues = formData.getAll(conditionFieldName + '[]')
      if (formValues.length === 0) return false
      if (Array.isArray(conditionValue)) {
        return conditionValue.some(cValue => isMatched(cValue, formValues))
      }
      return formValues.some(fDValue => conditionValue === String(fDValue))
    }
    // Single value on the form for this field
    const formValue = String(formData.get(conditionFieldName))
    return isMatched(formValue, conditionValue)
  }

export default class extends Controller<HTMLDivElement> {
  static targets = ['formShortcutInput', 'template', 'contentHolder']
  static values = {
    payload: Object
  }

  declare formShortcutInputTarget: HTMLInputElement
  declare templateTarget: HTMLTemplateElement
  declare contentHolderTarget: HTMLDivElement

  declare payloadValue: EmptyObject | { mode: 'all' | 'any'; conditions: Conditions }

  form: HTMLFormElement | null = null

  connect(): void {
    this.form = this.formShortcutInputTarget.form
    if (!this.#hasValuesAndInputs()) return
    listenToChangesOnForm(this.form, this.#checkInputValueChangesWithinBroadcast.bind(this))
  }

  #hasValuesAndInputs(): boolean {
    return Object.keys(this.payloadValue).length > 0 && this.form !== null
  }

  #checkInputValueChangesWithinBroadcast(
    eventPayload: CustomChangeBroadcastPayload | null = null
  ): void {
    if (!this.#hasValuesAndInputs()) return

    const isMultiple = fieldIsMultiple(eventPayload)
    const { mode, conditions } = this.payloadValue

    // 1: Assess whether we should check anything at all
    if (shouldSkipCheckForChanges(eventPayload, isMultiple, Object.keys(conditions))) return

    // 2: Check the conditions and hide/show the element
    const shouldBeVisible = this.#shouldElementBeVisible(mode, conditions, isMultiple)

    // 3: Show/hide the element
    if (shouldBeVisible) {
      if (this.templateTarget.dataset.isVisible === 'false') {
        const newElements = this.templateTarget.content.cloneNode(true)
        this.contentHolderTarget.appendChild(newElements)
        this.templateTarget.dataset.isVisible = 'true'
      }
    } else {
      if (this.templateTarget.dataset.isVisible === 'true') {
        this.contentHolderTarget.innerHTML = ''
        this.templateTarget.dataset.isVisible = 'false'
      }
    }
  }

  // deeper helpers

  #shouldElementBeVisible(
    mode: 'all' | 'any' | 'except',
    conditions: Conditions,
    isMultiple: boolean
  ): boolean {
    const method = mode !== 'any' ? 'every' : 'some'
    const formData = new FormData(this.form ?? undefined)
    const checkConditions = conditionChecker(isMultiple, formData)
    const isFound = Object.entries(conditions)[method](checkConditions)
    return mode === 'except' ? !isFound : isFound
  }
}
